import { HttpErrorResponse, HttpEvent, HttpHandlerFn, HttpInterceptorFn, HttpRequest } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { BehaviorSubject, Observable, catchError, filter, switchMap, take, throwError } from 'rxjs';

import { AuthService } from '../services/auth.service';
import { TokenStorageService } from '../services/token-storage.service';

/**
 * Rutas de autenticación que nunca deben llevar Bearer token ni disparar el
 * flujo de refresh (son las que gestionan el propio ciclo de vida del token).
 */
const AUTH_ENDPOINTS = [
  '/authentication/sign-in',
  '/authentication/refresh',
  '/authentication/login-firebase',
  '/authentication/forgot-password',
  '/authentication/verify-otp',
  '/authentication/reset-password'
];

// Estado compartido entre invocaciones del interceptor (closure de módulo):
// evita disparar múltiples refresh en paralelo cuando varias peticiones
// reciben 401 al mismo tiempo — todas esperan el mismo refresh en curso.
let isRefreshing = false;
const refreshedAccessToken$ = new BehaviorSubject<string | null>(null);

function isAuthEndpoint(url: string): boolean {
  return AUTH_ENDPOINTS.some((path) => url.includes(path));
}

function withBearer(req: HttpRequest<unknown>, token: string): HttpRequest<unknown> {
  return req.clone({ setHeaders: { Authorization: `Bearer ${token}` } });
}

/**
 * Interceptor funcional (HttpInterceptorFn):
 * 1. Adjunta el Bearer token a toda petición que no sea de autenticación.
 * 2. Ante un 401, intenta renovar la sesión (Refresh Token) UNA sola vez y
 *    reintenta automáticamente la petición original.
 * 3. Si el refresh también falla (refresh token vencido/inválido), limpia
 *    la sesión y redirige a /login.
 *
 * IMPORTANTE: inject() solo es válido durante la invocación síncrona del
 * interceptor — por eso las dependencias se resuelven aquí arriba y se pasan
 * como parámetros a las funciones auxiliares, nunca se llama inject() dentro
 * de un callback asíncrono (catchError/switchMap), donde ya no hay contexto
 * de inyección activo.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
  const tokenStorage = inject(TokenStorageService);
  const authService = inject(AuthService);
  const router = inject(Router);

  const accessToken = tokenStorage.getAccessToken();
  const authorizedReq =
    accessToken && !isAuthEndpoint(req.url) ? withBearer(req, accessToken) : req;

  return next(authorizedReq).pipe(
    catchError((error: HttpErrorResponse) => {
      if (error.status === 401 && !isAuthEndpoint(req.url)) {
        return handleUnauthorized(authorizedReq, next, authService, tokenStorage, router);
      }
      return throwError(() => error);
    })
  );
};

function handleUnauthorized(
  req: HttpRequest<unknown>,
  next: HttpHandlerFn,
  authService: AuthService,
  tokenStorage: TokenStorageService,
  router: Router
): Observable<HttpEvent<unknown>> {
  if (!isRefreshing) {
    isRefreshing = true;
    refreshedAccessToken$.next(null);

    const refreshToken = tokenStorage.getRefreshToken();
    if (!refreshToken) {
      isRefreshing = false;
      return forceLogout(authService, router);
    }

    return authService.refreshToken({ refreshToken }).pipe(
      switchMap((response) => {
        isRefreshing = false;
        refreshedAccessToken$.next(response.accessToken);
        return next(withBearer(req, response.accessToken));
      }),
      catchError(() => {
        isRefreshing = false;
        return forceLogout(authService, router);
      })
    );
  }

  // Ya hay un refresh en curso disparado por otra petición: esperamos su resultado.
  return refreshedAccessToken$.pipe(
    filter((token): token is string => token !== null),
    take(1),
    switchMap((token) => next(withBearer(req, token)))
  );
}

function forceLogout(authService: AuthService, router: Router): Observable<never> {
  authService.logout();
  router.navigate(['/login']);
  return throwError(() => new Error('La sesión expiró. Por favor, inicia sesión nuevamente.'));
}
